/**
 * Head metadata for the DealHunt-backed routes (PDP, category, search, coverage).
 *
 * WordPress-backed SoundGuys pages get their whole head from Yoast via
 * `headPropsMapper` (`json_ld` + a `meta` array). The DealHunt routes have no Yoast
 * payload, so without this builder they shipped `metaTags: []` — no description, no
 * Open Graph, no Twitter card, and none of the robots directives every other page on
 * the site carries. That means a bare link preview when shared, and no large image
 * preview in SERPs or Discover.
 *
 * The emitted tag set mirrors what Yoast produces for a normal SoundGuys page so the
 * two halves of the site look identical to a crawler. `og:url` is deliberately NOT
 * emitted here — `PageHead` already derives it from `canonicalLink`, and emitting it
 * twice would let the two drift.
 */
import type { DSDataLayer, DSMeta } from 'shared-definitions/types'
import type { CatalogPageType } from 'shared-code/app-events'

/** Matches the `og:site_name` Yoast emits site-wide. */
const SITE_NAME = 'SoundGuys'
/** Matches the `twitter:site` Yoast emits site-wide. */
const TWITTER_SITE = '@realsoundguys'

/**
 * The directive every WordPress page already carries. `max-image-preview:large` is the
 * load-bearing part on a product page — without it Google shows a thumbnail, or nothing,
 * instead of the product shot. It is NOT set as an HTTP header anywhere on this site, so
 * a page that omits it simply loses it.
 */
const ROBOTS = 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'

/** Google truncates around 155–160 chars; trim on a word boundary rather than mid-word. */
const DESCRIPTION_MAX = 158

export function clampDescription(text: string, max: number = DESCRIPTION_MAX): string {
  const clean = text.replace(/\s+/g, ' ').trim()
  if (clean.length <= max) {
    return clean
  }
  const cut = clean.slice(0, max)
  const lastSpace = cut.lastIndexOf(' ')
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[,;:.\s]+$/, '')}…`
}

export interface HeadMetaInput {
  /** The `<title>`; also used for `og:title`. */
  title: string
  /** Meta description and `og:description`. Omitted entirely when null — never invented. */
  description?: string | null
  /** Absolute image URL for `og:image`. Omitted when null. */
  imageUrl?: string | null
  /** `og:type` — `product` for a PDP, `website` for listings. */
  ogType?: 'product' | 'website'
  /** Set false on pages that must not be indexed (internal search). */
  index?: boolean
}

/**
 * Build the `metaTags` array for a DealHunt route.
 *
 * Absent inputs emit no tag rather than a placeholder: a wrong description or a
 * missing-image URL is worse in a SERP than the tag being absent.
 */
export function buildMetaTags({
  title,
  description = null,
  imageUrl = null,
  ogType = 'website',
  index = true,
}: HeadMetaInput): DSMeta[] {
  const desc = description ? clampDescription(description) : null
  const tags: DSMeta[] = [
    { name: 'robots', content: index ? ROBOTS : 'noindex, follow' },
    { property: 'og:locale', content: 'en_US' },
    { property: 'og:type', content: ogType },
    { property: 'og:title', content: title },
    { property: 'og:site_name', content: SITE_NAME },
    { name: 'twitter:site', content: TWITTER_SITE },
    // `summary_large_image` only when there IS an image — Twitter falls back to a bare
    // card if the declared large image is missing, which looks broken.
    { name: 'twitter:card', content: imageUrl ? 'summary_large_image' : 'summary' },
  ]
  if (desc) {
    tags.push({ name: 'description', content: desc })
    tags.push({ property: 'og:description', content: desc })
  }
  if (imageUrl) {
    tags.push({ property: 'og:image', content: imageUrl })
  }
  return tags
}

/**
 * "Plus 1 owner review." / "Plus 12 owner reviews." — the owners clause the SERP
 * snippet gains when readers have written in. Counts only, never a percentage and never a
 * sentiment word (owner-notes #52/#54): the snippet says how many wrote, not what they said.
 */
function ownersClause(count: number): string {
  return count === 1 ? 'Plus 1 owner review.' : `Plus ${count} owner reviews.`
}

/**
 * Append the clause only while the whole line still fits a SERP snippet — a clause clamped
 * to "Plus 12 owner rev…" is worse than no clause at all. A base that does not end a
 * sentence gets the terminator the join needs; its own words are never touched.
 */
function withOwnersClause(base: string, clause: string): string {
  if (!clause) {
    return base
  }
  const trimmed = base.trim()
  const joined = `${trimmed}${/[.!?…]$/.test(trimmed) ? '' : '.'} ${clause}`
  return joined.length <= DESCRIPTION_MAX ? joined : base
}

/**
 * Meta description for a product page.
 *
 * `node.seoDescription` is DealHunt's purpose-built SEO field — it arrives on the wire
 * and was previously read by nothing. `tagline` (the review's subtitle) is the next-best
 * human-written line. The generated fallback is last and stays deliberately factual:
 * it only claims a SoundGuys rating when there actually is one.
 *
 * Third-party review text (`enrichment.summary`, which carries another publication's
 * `sourceName`/`authorName`) is intentionally NOT in this chain — it would put another
 * outlet's words in SoundGuys' SERP snippet.
 */
export function productDescription(input: {
  seoDescription: string | null | undefined
  tagline: string | null | undefined
  fullName: string
  score: number | null
  /**
   * `enrichment.owners.published_count` (SEO audit 2026-09-18). From one published note the
   * snippet says so — the notes are already in the page's HTML and nothing else in the
   * snippet tells a searcher they are there. Passed for the META description only: the
   * JSON-LD `description` is built from the clause-free line, because no owner data of any
   * kind goes into structured data (contract #13).
   */
  ownerCount?: number | null
}): string {
  const clause = input.ownerCount && input.ownerCount >= 1 ? ownersClause(input.ownerCount) : ''
  // The editor's own SEO field is theirs whole — the clause never extends it.
  const authored = input.seoDescription?.trim()
  if (authored) {
    return clampDescription(authored)
  }
  const tagline = input.tagline?.trim()
  if (tagline) {
    return clampDescription(withOwnersClause(tagline, clause))
  }
  const rating =
    input.score !== null
      ? ` Rated ${input.score.toFixed(1)}/10 in the SoundGuys review.`
      : ' Compare the specs, scores and price against similar gear on SoundGuys.'
  return clampDescription(
    withOwnersClause(`${input.fullName}: full specifications and pricing.${rating}`, clause)
  )
}

/**
 * GTM datalayer for an app-owned (non-WordPress) page.
 *
 * WP pages ship theirs in the API payload (`head.datalayer` → `metaMapper`), and the GTM
 * `page-view` push re-fires on the CHANGED IDENTITY of `PostContext.dataLayer` (see
 * `shared-components/thirdparty/GTM.tsx`). App-owned pages that pass `null` therefore
 * lose twice: entry pageviews carry none of the metadata WP pages send (author, category,
 * title…), and client-side navigations between two app-owned pages — all served by the
 * same Next page file, so nothing remounts — never re-fire the push at all (`null` →
 * `null` is not a change), making the navigation invisible to GA4.
 *
 * Key names follow the WP datalayer vocabulary (`title`, `category`) so existing GTM
 * variable mappings pick them up unchanged; `page_type` and `brand` are catalog-only
 * additions (map them in GTM when wanted). `current_post_url` differs between any two
 * pages, so every navigation is guaranteed a fresh identity → a fresh `page-view`.
 *
 * Callers must keep the object referentially stable across re-renders (`useMemo`) —
 * a new object per render would re-fire the push without a navigation.
 */
export function catalogDataLayer(input: {
  /**
   * The catalog surfaces share `CatalogPageType` with the interaction events
   * (`product_page_*`…) so the page-view and event pushes agree on ONE `page_type`
   * vocabulary in GA4. `search` is the editorial posts search (`post_search`) — not a catalog surface.
   */
  pageType: CatalogPageType | 'search'
  title: string
  /** The page's canonical URL (or path) — the identity carrier described above. */
  url: string
  category?: string | null
  brand?: string | null
}): DSDataLayer {
  return {
    page_type: input.pageType,
    title: input.title,
    category: input.category ?? '',
    brand: input.brand ?? '',
    current_post_url: input.url,
  }
}

/** Meta description for a category landing/browse page. */
export function categoryDescription(input: {
  seoDescription: string | null | undefined
  name: string
}): string {
  const authored = input.seoDescription?.trim()
  if (authored) {
    return clampDescription(authored)
  }
  return clampDescription(
    `Browse ${input.name} on SoundGuys — compare specs, lab-measured scores and current prices.`
  )
}
